import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Tag, Button, Space, Tooltip, Empty, Spin, Badge, Dropdown, Modal, message
} from 'antd';
import {
  EditOutlined, EyeOutlined, DeleteOutlined, CopyOutlined,
  CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined,
  MoreOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import templateService from 'services/templateService';

/**
 * KanbanView - Kanban board for template status management
 * Features:
 * - Columns: Active, Draft, Inactive/Archived
 * - Drag and drop (future enhancement)
 * - Visual status indicators
 * - Quick status changes
 */
const KanbanView = ({ filters, onEdit, onPreview, onViewStats, onViewHistory, onRefresh }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Organize templates by status
  const [columns, setColumns] = useState({
    active: [],
    draft: [],
    inactive: [],
  });

  const loadTemplates = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await templateService.getTemplates({
        ...filters,
        pageSize: 100, // Load more for kanban view
      });

      if (response.success) {
        setTemplates(response.data.templates || response.data || []);
      }
    } catch (error) {
      console.error('Kanban view error:', error);
      message.error('Failed to load templates. Using offline mode.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const organizeTemplates = React.useCallback(() => {
    const organized = {
      active: [],
      draft: [],
      inactive: [],
    };

    templates.forEach(template => {
      if (template.isDraft) {
        organized.draft.push(template);
      } else if (template.isActive) {
        organized.active.push(template);
      } else {
        organized.inactive.push(template);
      }
    });

    setColumns(organized);
  }, [templates]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    organizeTemplates();
  }, [organizeTemplates]);

  const handleStatusChange = async (template, newStatus) => {
    try {
      const updates = {};

      if (newStatus === 'active') {
        updates.isActive = true;
        updates.isDraft = false;
      } else if (newStatus === 'draft') {
        updates.isDraft = true;
      } else if (newStatus === 'inactive') {
        updates.isActive = false;
        updates.isDraft = false;
      }

      await templateService.updateTemplate(template.templateId, updates);
      message.success('Status updated successfully');
      onRefresh?.();
      loadTemplates();
    } catch (error) {
      message.error('Failed to update status: ' + error.message);
    }
  };

  const handleDelete = async (template) => {
    Modal.confirm({
      title: 'Delete Template',
      content: `Are you sure you want to delete "${template.templateName}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await templateService.deleteTemplate(template.templateId);
          message.success('Template deleted successfully');
          onRefresh?.();
          loadTemplates();
        } catch (error) {
          message.error('Failed to delete template: ' + error.message);
        }
      },
    });
  };

  const handleClone = async (template) => {
    try {
      const clonedTemplate = {
        ...template,
        templateName: `${template.templateName} (Copy)`,
        isDefault: false,
        isDraft: true, // New clones start as drafts
      };
      delete clonedTemplate.templateId;

      await templateService.createTemplate(clonedTemplate);
      message.success('Template cloned successfully');
      onRefresh?.();
      loadTemplates();
    } catch (error) {
      message.error('Failed to clone template: ' + error.message);
    }
  };

  const getMenuItems = (template, currentStatus) => {
    const items = [
      {
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => onEdit?.(template),
      },
      {
        key: 'preview',
        label: 'Preview',
        icon: <EyeOutlined />,
        onClick: () => onPreview?.(template),
      },
      {
        key: 'clone',
        label: 'Clone',
        icon: <CopyOutlined />,
        onClick: () => handleClone(template),
      },
      {
        type: 'divider',
      },
    ];

    // Add status change options
    if (currentStatus !== 'active') {
      items.push({
        key: 'activate',
        label: 'Activate',
        icon: <CheckCircleOutlined />,
        onClick: () => handleStatusChange(template, 'active'),
      });
    }

    if (currentStatus !== 'draft') {
      items.push({
        key: 'draft',
        label: 'Move to Draft',
        icon: <FileTextOutlined />,
        onClick: () => handleStatusChange(template, 'draft'),
      });
    }

    if (currentStatus !== 'inactive') {
      items.push({
        key: 'inactive',
        label: 'Deactivate',
        icon: <CloseCircleOutlined />,
        onClick: () => handleStatusChange(template, 'inactive'),
      });
    }

    items.push(
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(template),
      }
    );

    return items;
  };

  const renderTemplateCard = (template, status) => (
    <TemplateCard key={template.templateId}>
      <CardHeader>
        <TemplateName>{template.templateName}</TemplateName>
        <Dropdown
          menu={{ items: getMenuItems(template, status) }}
          trigger={['click']}
        >
          <Button type="text" size="small" icon={<MoreOutlined />} />
        </Dropdown>
      </CardHeader>

      <CardBody>
        <DescriptionText>
          {template.description || 'No description'}
        </DescriptionText>

        <TagsContainer>
          <Tag color={getLevelColor(template.analysisLevel)}>
            {template.analysisLevel}
          </Tag>
          {template.isDefault && <Tag color="gold">Default</Tag>}
          {template.scope && (
            <Tag>{getScopeLabel(template.scope)}</Tag>
          )}
        </TagsContainer>

        {template.category && (
          <CategoryBadge>#{template.category}</CategoryBadge>
        )}

        <MetaInfo>
          <MetaItem>v{template.version || 1}</MetaItem>
          <MetaItem>
            {template.createdAt && new Date(template.createdAt).toLocaleDateString()}
          </MetaItem>
        </MetaInfo>
      </CardBody>

      <CardFooter>
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit?.(template)}
        >
          Edit
        </Button>
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onPreview?.(template)}
        >
          Preview
        </Button>
      </CardFooter>
    </TemplateCard>
  );

  const renderColumn = (title, templates, status, icon, color) => (
    <Column>
      <ColumnHeader color={color}>
        <ColumnTitle>
          {icon}
          <span>{title}</span>
          <Badge count={templates.length} />
        </ColumnTitle>
      </ColumnHeader>
      <ColumnBody>
        {templates.length === 0 ? (
          <EmptyState>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No ${title.toLowerCase()}`}
            />
          </EmptyState>
        ) : (
          templates.map(template => renderTemplateCard(template, status))
        )}
      </ColumnBody>
    </Column>
  );

  const getLevelColor = (level) => {
    const colors = {
      'page': 'blue',
      'module': 'green',
      'dashboard': 'orange',
      'hrst': 'purple',
    };
    return colors[level] || 'default';
  };

  const getScopeLabel = (scope) => {
    const labels = {
      'SYSTEM': 'System',
      'ORGANIZATION': 'Organization',
      'USER': 'User',
    };
    return labels[scope] || scope;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Loading templates..." />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Board>
        {renderColumn(
          'Active',
          columns.active,
          'active',
          <CheckCircleOutlined />,
          '#52c41a'
        )}
        {renderColumn(
          'Draft',
          columns.draft,
          'draft',
          <FileTextOutlined />,
          '#faad14'
        )}
        {renderColumn(
          'Inactive',
          columns.inactive,
          'inactive',
          <CloseCircleOutlined />,
          '#d9d9d9'
        )}
      </Board>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 24px 0;
  overflow-x: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const Board = styled.div`
  display: flex;
  gap: 24px;
  min-width: 1000px;
`;

const Column = styled.div`
  flex: 1;
  min-width: 320px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 250px);
`;

const ColumnHeader = styled.div`
  padding: 16px;
  background: white;
  border-bottom: 3px solid ${props => props.color};
  border-radius: 8px 8px 0 0;
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;

  .anticon {
    font-size: 18px;
    color: ${props => props.color};
  }

  span {
    flex: 1;
  }
`;

const ColumnBody = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }

  &::-webkit-scrollbar-thumb {
    background: #bfbfbf;
    border-radius: 3px;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
`;

const TemplateCard = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 12px 8px 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const TemplateName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 8px;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const DescriptionText = styled.div`
  color: #595959;
  font-size: 13px;
  line-height: 1.4;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 8px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

const CategoryBadge = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  font-style: italic;
  margin-bottom: 8px;
`;

const MetaInfo = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`;

const MetaItem = styled.div`
  color: #8c8c8c;
  font-size: 12px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
`;

export default KanbanView;
